import React, {
  useState, KeyboardEvent, useRef, useEffect, useCallback,
} from 'react';
import { useQnAContext } from './contexts/QnAContext';
import styles from './QnAWidget.module.scss';

const MAX_INPUT_LENGTH = 255;
const LINE_HEIGHT = 20;
const TEXTAREA_PADDING = 16;
const MIN_ROWS = 1;
const MAX_ROWS = 4;

export function QnAInputArea() {
  const {
    isExpanded,
    sendMessage,
    isAIBusy,
    isRetrying,
    contextLoading,
  } = useQnAContext();

  const [inputValue, setInputValue] = useState('');

  const inputRef = useRef<HTMLTextAreaElement>(null);

  const isDisabled = isAIBusy || isRetrying || contextLoading;
  const characterCount = inputValue.length;
  const isOverLimit = characterCount >= MAX_INPUT_LENGTH;

  const adjustTextareaHeight = useCallback(() => {
    if (inputRef.current) {
      const { current: textarea } = inputRef;
      textarea.style.height = 'auto';

      const { scrollHeight } = textarea;
      const rows = Math.min(MAX_ROWS, Math.max(MIN_ROWS, Math.ceil((scrollHeight - TEXTAREA_PADDING) / LINE_HEIGHT)));

      textarea.style.height = `${rows * LINE_HEIGHT + TEXTAREA_PADDING}px`;
    }
  }, []);

  const getPlaceholder = () => {
    if (contextLoading) return 'Loading context...';
    if (isRetrying) return 'Retrying...';
    if (isAIBusy) return 'AI is thinking...';
    return 'Ask me anything...';
  };

  const getLoadingText = () => {
    if (contextLoading) return 'Loading context...';
    if (isRetrying) return 'Retrying...';
    return 'AI is thinking...';
  };

  useEffect(() => {
    if (!isDisabled && isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isDisabled, isExpanded]);

  // Focus input when widget expands, blur when it collapses
  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    } else if (!isExpanded && inputRef.current) {
      inputRef.current.blur();
    }
  }, [isExpanded]);

  // Adjust height when input value changes
  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue, adjustTextareaHeight]);

  const handleSend = useCallback(async () => {
    if (inputValue.trim() && !isDisabled && !isOverLimit) {
      const message = inputValue.trim();
      const success = await sendMessage(message);

      if (success) {
        setInputValue('');
      }
    }
  }, [inputValue, isDisabled, isOverLimit, sendMessage]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    adjustTextareaHeight();
  }, [adjustTextareaHeight]);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  return (
    <div className={styles.inputArea}>
      <div className={styles.inputContainer}>
        <textarea
          ref={inputRef}
          className={`${styles.messageInput} ${isOverLimit ? styles.inputError : ''}`}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={getPlaceholder()}
          disabled={isDisabled}
          maxLength={MAX_INPUT_LENGTH}
          rows={1}
        />
        <div className={styles.characterCounter}>
          <span className={isOverLimit ? styles.counterError : styles.counterNormal}>
            {characterCount}
            /
            {MAX_INPUT_LENGTH}
          </span>
        </div>
        <button
          className={styles.sendButton}
          onClick={handleSend}
          disabled={isDisabled || !inputValue.trim() || isOverLimit}
          title="Send message"
          type="button"
        >
          ➤
        </button>

        {isDisabled && (
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingIndicator}>
              <div className={styles.loadingDots}>
                <div className={styles.loadingDot} />
                <div className={styles.loadingDot} />
                <div className={styles.loadingDot} />
              </div>
              <span className={styles.loadingText}>
                {getLoadingText()}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
